/**
 * Shared ordering and coverage-result pairing for Postgres-backed coverage tabs.
 * Ensures multiple rows per tier (e.g. two primaries) stay aligned with
 * parallel `coverage_results` rows of the same type.
 */
import type {
  CoverageResult,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";

/** Single ordered coverage row with its index-aligned result. */
export interface CoverageRowWithResult {
  coverage: SingleCoverage;
  coverageResult?: CoverageResult;
}

/** One top-level plan tab, containing one or more patient-responsibility rows. */
export interface GroupedCoverageTab {
  key: string;
  representativeCoverage: SingleCoverage;
  representativeCoverageResult?: CoverageResult;
  rows: CoverageRowWithResult[];
}

/** Canonical tab order for insurance tiers. */
export const PREFERRED_COVERAGE_ORDER = [
  "primary",
  "secondary",
  "tertiary",
] as const;

/**
 * Type guard for known Postgres `coverage_type` values.
 *
 * @param value - Raw coverage type string
 * @returns True when value is primary, secondary, or tertiary
 */
function isPreferredCoverageType(
  value: string | undefined,
): value is (typeof PREFERRED_COVERAGE_ORDER)[number] {
  return (
    value !== undefined &&
    (PREFERRED_COVERAGE_ORDER as readonly string[]).includes(value)
  );
}

/**
 * Orders coverage rows for the UI: all primary rows (input order), then
 * secondary, then tertiary; rows with missing or unknown `coverage_type`
 * are appended last.
 *
 * @param coverages - Raw coverage rows from the API
 * @returns Rows ordered for display tabs
 */
export function getOrderedCoveragesForDisplay(
  coverages: SingleCoverage[],
): SingleCoverage[] {
  if (!coverages?.length) {
    return [];
  }

  const tierOrdered = PREFERRED_COVERAGE_ORDER.flatMap((key) =>
    coverages.filter((c) => c.coverage_type === key),
  );
  const remaining = coverages.filter(
    (c) => !isPreferredCoverageType(c.coverage_type),
  );
  return [...tierOrdered, ...remaining];
}

/**
 * Selects the coverage_result row for a coverage tab when several rows share
 * the same `coverage_type`, using occurrence index within `orderedCoverages`.
 *
 * @param orderedCoverages - Full ordered tab list (same order as tabs)
 * @param coverageRowIndex - Index of the current row in that list
 * @param coverageResults - All coverage_result rows from the API
 * @returns Matching result or undefined when none exist at that index
 */
export function getCoverageResultForCoverageRow(
  orderedCoverages: SingleCoverage[],
  coverageRowIndex: number,
  coverageResults: CoverageResult[],
): CoverageResult | undefined {
  const coverage = orderedCoverages[coverageRowIndex];
  const coverageType = coverage?.coverage_type;
  if (!coverageType) {
    return undefined;
  }
  const indexAmongType = orderedCoverages
    .slice(0, coverageRowIndex)
    .filter((c) => c.coverage_type === coverageType).length;
  const matches = coverageResults.filter(
    (r) => r.coverage_type === coverageType,
  );
  return matches[indexAmongType];
}

/**
 * Generates a stable grouping key for top-level insurance tabs.
 * Coverage rows with the same key are shown under one tab and their
 * patient-responsibility rows are rendered as treatment-level tabs below.
 *
 * @param coverage - Ordered coverage row
 * @returns Grouping key for plan-level tab aggregation
 */
function buildCoveragePlanGroupingKey(coverage: SingleCoverage): string {
  const type = coverage?.coverage_type?.toLowerCase().trim() ?? "";
  const payer = coverage?.payer_name?.toLowerCase().trim() ?? "";
  const plan = coverage?.plan_name?.toLowerCase().trim() ?? "";
  const member = coverage?.member_id?.toLowerCase().trim() ?? "";
  return `${type}|${payer}|${plan}|${member}`;
}

/**
 * Groups ordered coverage rows by plan identity (tier + payer + plan + member).
 * Each returned group becomes a single top-level insurance tab.
 *
 * @param coverages - Raw coverage rows from the API
 * @param coverageResults - Raw coverage result rows from the API
 * @returns Ordered plan groups with all patient-responsibility rows preserved
 */
export function getGroupedCoverageTabsForDisplay(
  coverages: SingleCoverage[],
  coverageResults: CoverageResult[],
): GroupedCoverageTab[] {
  const orderedCoverages = getOrderedCoveragesForDisplay(coverages);
  const grouped = new Map<string, GroupedCoverageTab>();

  orderedCoverages.forEach((coverage, rowIndex) => {
    const coverageResult = getCoverageResultForCoverageRow(
      orderedCoverages,
      rowIndex,
      coverageResults,
    );
    const key = buildCoveragePlanGroupingKey(coverage);
    const existing = grouped.get(key);
    if (existing) {
      existing.rows.push({ coverage, coverageResult });
      return;
    }
    grouped.set(key, {
      key,
      representativeCoverage: coverage,
      representativeCoverageResult: coverageResult,
      rows: [{ coverage, coverageResult }],
    });
  });

  return Array.from(grouped.values());
}
